import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/useAuth";
import { useTranslation } from "../contexts/TranslationContext";
import { getHomePathForRole } from "../utils/roleHome";

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { token, isAdmin, isPaid, fetchCredits } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const homePath = getHomePathForRole({ isAdmin, isPaid });
  const [status, setStatus] = useState("loading");
  const [details, setDetails] = useState(null);
  const retryCount = useRef(0);
  const maxRetries = 10;

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || !token) return;

    let timeoutId;

    async function checkSession() {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/stripe/session/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        if (data.status === "paid") {
          setStatus("success");
          setDetails(data);
          fetchCredits(token);
        } else if (retryCount.current < maxRetries) {
          retryCount.current += 1;
          timeoutId = setTimeout(checkSession, 3000);
        } else {
          setStatus("pending");
        }
      } catch {
        if (retryCount.current < maxRetries) {
          retryCount.current += 1;
          timeoutId = setTimeout(checkSession, 3000);
        } else {
          setStatus("error");
        }
      }
    }

    checkSession();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [searchParams, token]);

  return (
    <div className="flex-1 flex items-center justify-center px-4">
      <div className="bg-[#111] border border-white/10 p-8 rounded-xl shadow-lg text-center max-w-md w-full">
        {status === "loading" && (
          <div>
            <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-300">{t('payment.checking')}</p>
          </div>
        )}

        {status === "success" && (
          <>
            <h1 className="text-2xl font-bold text-green-400 mb-4">{t('payment.successTitle')}</h1>
            <p className="text-gray-300 mb-2">
              {details?.credits
                ? `${details.credits} ${t('payment.creditsAdded')}`
                : t('payment.creditsAddedGeneric')}
            </p>
            <button
              onClick={() => navigate(homePath)}
              className="mt-4 w-full bg-brand-500 hover:bg-brand-400 text-black font-bold py-3 px-4 rounded-md transition"
            >
              {t('payment.backHome')}
            </button>
          </>
        )}

        {status === "pending" && (
          <>
            <h1 className="text-2xl font-bold text-brand-500 mb-4">{t('payment.pendingTitle')}</h1>
            <p className="text-gray-300">{t('payment.pendingMessage')}</p>
            <button
              onClick={() => navigate(homePath)}
              className="mt-4 w-full bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-4 rounded-md transition"
            >
              {t('payment.backHome')}
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="text-2xl font-bold text-red-400 mb-4">{t('payment.errorTitle')}</h1>
            <p className="text-gray-300">{t('payment.errorMessage')}</p>
            <button
              onClick={() => navigate(homePath)}
              className="mt-4 w-full bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-4 rounded-md transition"
            >
              {t('payment.backHome')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentSuccess;
